import React, { useState } from "react";
import { formatEnumValue } from "@/lib/formatters";

type Filters = {
  type: string[];
  pattern: string[];
  yearRange?: [number, number];
};

type FilterOptions = {
  type: string[];
  pattern: string[];
  yearRange?: [number, number];
};

type FilterSortPanelUIProps = {
  filters: Filters;
  onFilterChange: (filterGroup: keyof Filters, value: string, checked: boolean) => void;
  filterOptions: FilterOptions;
  sortBy: string;
  onSetSortBy: (sortKey: string) => void;
  horizontal?: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearFilters: () => void;
  onYearRangeChange?: (range: [number, number]) => void;
};

export default function FilterSortPanelUI({
  filters,
  onFilterChange,
  filterOptions,
  sortBy,
  onSetSortBy,
  horizontal = false,
  searchTerm,
  onSearchChange,
  onClearFilters,
  onYearRangeChange,
}: FilterSortPanelUIProps) {
  const [yearRange, setYearRange] = useState<[number, number]>(
    filters.yearRange || filterOptions.yearRange || [1920, 2024]
  );

  // Update local yearRange when filters.yearRange changes
  React.useEffect(() => {
    if (filters.yearRange) {
      setYearRange(filters.yearRange);
    } else if (filterOptions.yearRange) {
      setYearRange(filterOptions.yearRange);
    }
  }, [filters.yearRange, filterOptions.yearRange]);
  const [isDragging, setIsDragging] = useState<number | null>(null);
  const [sliderRef, setSliderRef] = useState<HTMLDivElement | null>(null);

  const minYear = filterOptions.yearRange?.[0] || 1920;
  const maxYear = filterOptions.yearRange?.[1] || 2024;
  const yearSpan = maxYear - minYear;

  const handleYearRangeChange = React.useCallback((index: number, value: number) => {
    const newRange: [number, number] = [...yearRange];
    newRange[index] = value;
    
    // Ensure start year is not greater than end year
    if (index === 0 && value > newRange[1]) {
      newRange[1] = value;
    } else if (index === 1 && value < newRange[0]) {
      newRange[0] = value;
    }
    
    setYearRange(newRange);
    onYearRangeChange?.(newRange);
  }, [yearRange, onYearRangeChange]);

  const handleMouseDown = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(index);
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (isDragging === null || !sliderRef) return;
    
    const rect = sliderRef.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newYear = Math.round(minYear + (percent * yearSpan));
    
    handleYearRangeChange(isDragging, newYear);
  }, [isDragging, sliderRef, minYear, yearSpan, handleYearRangeChange]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(null);
  }, []);

  React.useEffect(() => {
    if (isDragging !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!horizontal) {
    return (
      <div className="flex flex-col gap-4 font-ui px-6">
        {/* Search Box */}
        <div className="flex flex-col gap-2">
          <input
            type="text"
            placeholder="Search microphones..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="p-2 bg-white/10 border border-white/30 rounded text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
          />
        </div>

        {/* Sort Dropdown */}
        <div className="flex flex-col gap-2">
          <select
            value={sortBy}
            onChange={(e) => onSetSortBy(e.target.value)}
            className="p-2 bg-white/10 border border-white/30 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/50 cursor-pointer"
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
          >
            <option value="name" style={{ backgroundColor: '#374151', color: 'white' }}>Name</option>
            <option value="year" style={{ backgroundColor: '#374151', color: 'white' }}>Year of Manufacture</option>
            <option value="type" style={{ backgroundColor: '#374151', color: 'white' }}>Type</option>
            <option value="pattern" style={{ backgroundColor: '#374151', color: 'white' }}>Pattern</option>
            <option value="dateAdded" style={{ backgroundColor: '#374151', color: 'white' }}>Date Added (Newest)</option>
            <option value="dateAddedOldest" style={{ backgroundColor: '#374151', color: 'white' }}>Date Added (Oldest)</option>
          </select>
        </div>

        {/* Filters */}
        {(['type', 'pattern'] as const).map((filterGroup) => (
          <div key={filterGroup} className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-white/70 capitalize">
              {filterGroup}:
            </label>
            <div className="flex flex-col gap-1">
              {filterOptions[filterGroup]?.map((option) => (
                <label key={option} className="flex items-center gap-2 cursor-pointer text-sm text-white hover:text-white/80 transition-colors">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={filters[filterGroup]?.includes(option) || false}
                      onChange={(e) => onFilterChange(filterGroup, option, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                      filters[filterGroup]?.includes(option)
                        ? 'bg-white border-white'
                        : 'bg-white/10 border-white/30 hover:border-white/50'
                    }`}>
                      {filters[filterGroup]?.includes(option) && (
                        <svg className="w-3 h-3 text-gray-900 absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span>{formatEnumValue(option)}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Clear Button */}
        {(searchTerm !== "" || 
          filters.type.length > 0 || 
          filters.pattern.length > 0 || 
          sortBy !== "name" ||
          (filters.yearRange && filterOptions.yearRange && 
           (filters.yearRange[0] !== filterOptions.yearRange[0] || filters.yearRange[1] !== filterOptions.yearRange[1]))) && (
          <button
            onClick={onClearFilters}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1 cursor-pointer"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Clear
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 font-ui px-6">
      {/* First Row: Search, Sort, Date Slider, Clear Button */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Search Box */}
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <label
              htmlFor="mic-search"
              className="text-sm font-semibold text-white/70"
            >
              Search:
            </label>
            <span id="mic-search-hint" className="text-xs text-white/50">
              (by make, model or country)
            </span>
          </div>
          <input
            id="mic-search"
            aria-describedby="mic-search-hint"
            type="text"
            placeholder="Search microphones..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="p-2 bg-white/10 border border-white/30 rounded text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
          />
        </div>

        {/* Sort Dropdown */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-white/70">Sort:</label>
          <select
            value={sortBy}
            onChange={(e) => onSetSortBy(e.target.value)}
            className="p-2 bg-white/10 border border-white/30 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/50 cursor-pointer"
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
              paddingRight: '2.5rem'
            }}
          >
            <option value="name" style={{ backgroundColor: '#374151', color: 'white' }}>Name</option>
            <option value="year" style={{ backgroundColor: '#374151', color: 'white' }}>Year of Manufacture</option>
            <option value="type" style={{ backgroundColor: '#374151', color: 'white' }}>Type</option>
            <option value="pattern" style={{ backgroundColor: '#374151', color: 'white' }}>Pattern</option>
            <option value="dateAdded" style={{ backgroundColor: '#374151', color: 'white' }}>Date Added (Newest)</option>
            <option value="dateAddedOldest" style={{ backgroundColor: '#374151', color: 'white' }}>Date Added (Oldest)</option>
          </select>
        </div>

        {/* Empty space where date slider was */}
        <div></div>

        {/* Clear Button */}
        <div className="flex flex-col gap-2 justify-end items-end">
          {(searchTerm !== "" || 
            filters.type.length > 0 || 
            filters.pattern.length > 0 || 
            sortBy !== "name" ||
            (filters.yearRange && filterOptions.yearRange && 
             (filters.yearRange[0] !== filterOptions.yearRange[0] || filters.yearRange[1] !== filterOptions.yearRange[1]))) && (
            <button
              onClick={onClearFilters}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded text-white text-sm font-medium transition-colors duration-200 flex items-center gap-1 cursor-pointer"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Second Row: Type Filter, Pattern Filter */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start">
        {/* Type Filter - Cell 1 */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-white/70">Type:</label>
          <div className="flex flex-col gap-2">
            {filterOptions.type.map((option) => (
              <label key={option} className="flex items-center gap-2 cursor-pointer text-sm text-white hover:text-white/80 transition-colors">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.type.includes(option)}
                    onChange={(e) => onFilterChange('type', option, e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                    filters.type.includes(option)
                      ? 'bg-white border-white'
                      : 'bg-white/10 border-white/30 hover:border-white/50'
                  }`}>
                    {filters.type.includes(option) && (
                      <svg className="w-3 h-3 text-gray-900 absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <span>{formatEnumValue(option)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Pattern Filter - Cell 2 */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-white/70">Pattern:</label>
          <div className="flex flex-col gap-2">
            {filterOptions.pattern.map((option) => (
              <label key={option} className="flex items-center gap-2 cursor-pointer text-sm text-white hover:text-white/80 transition-colors">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.pattern.includes(option)}
                    onChange={(e) => onFilterChange('pattern', option, e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded border-2 transition-all duration-200 ${
                    filters.pattern.includes(option)
                      ? 'bg-white border-white'
                      : 'bg-white/10 border-white/30 hover:border-white/50'
                  }`}>
                    {filters.pattern.includes(option) && (
                      <svg className="w-3 h-3 text-gray-900 absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </div>
                <span>{formatEnumValue(option)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Date Slider - Cell 3 */}
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-white/70 mb-[20px] text-left">Year Range:</label>
          <div className="flex flex-col justify-center h-full">
            <div className="w-3/5">
              <div 
                ref={setSliderRef}
                className="relative h-6 cursor-pointer"
                onClick={(e) => {
                  if (!sliderRef || isDragging !== null) return;
                  const rect = sliderRef.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  const newYear = Math.round(minYear + (percent * yearSpan));
                  
                  // Determine which handle is closer
                  const distToStart = Math.abs(newYear - yearRange[0]);
                  const distToEnd = Math.abs(newYear - yearRange[1]);
                  const closerHandle = distToStart <= distToEnd ? 0 : 1;
                  
                  handleYearRangeChange(closerHandle, newYear);
                }}
              >
                {/* Slider Track */}
                <div className="absolute top-1/2 left-0 right-0 h-2 bg-white/10 rounded-full transform -translate-y-1/2"></div>
                
                {/* Active Range */}
                <div 
                  className="absolute top-1/2 h-2 bg-white/40 rounded-full transform -translate-y-1/2"
                  style={{
                    left: `${((yearRange[0] - minYear) / yearSpan) * 100}%`,
                    width: `${((yearRange[1] - yearRange[0]) / yearSpan) * 100}%`
                  }}
                />
                
                {/* Start Marker */}
                <div 
                  className={`absolute top-1/2 w-6 h-6 bg-white border-2 border-gray-600 rounded-full cursor-grab shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors select-none transform -translate-y-1/2 ${isDragging === 0 ? 'cursor-grabbing scale-110' : ''}`}
                  style={{
                    left: `calc(${((yearRange[0] - minYear) / yearSpan) * 100}% - 12px)`
                  }}
                  onMouseDown={handleMouseDown(0)}
                >
                  <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                </div>
                
                {/* End Marker */}
                <div 
                  className={`absolute top-1/2 w-6 h-6 bg-white border-2 border-gray-600 rounded-full cursor-grab shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors select-none transform -translate-y-1/2 ${isDragging === 1 ? 'cursor-grabbing scale-110' : ''}`}
                  style={{
                    left: `calc(${((yearRange[1] - minYear) / yearSpan) * 100}% - 12px)`
                  }}
                  onMouseDown={handleMouseDown(1)}
                >
                  <div className="w-2 h-2 bg-gray-600 rounded-full"></div>
                </div>
              </div>
              
              {/* Year Labels */}
              <div className="relative text-xs text-white/70 mt-2 h-4">
                <span 
                  className="absolute"
                  style={{
                    left: `${((yearRange[0] - minYear) / yearSpan) * 100}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  {yearRange[0]}
                </span>
                <span 
                  className="absolute"
                  style={{
                    left: `${((yearRange[1] - minYear) / yearSpan) * 100}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  {yearRange[1]}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Empty Cell 4 */}
        <div></div>
      </div>
    </div>
  );
}
